import { FranchiseLocation } from '../franchise.model';
import { PackageItem } from './package-item.model';

import * as lodash from 'lodash';
import { formatCurrency } from '@angular/common';

export interface IOrderRequestItem {
  orderId?: string;
  selectedLocation?: FranchiseLocation;
  selectedPackageList?: PackageItem[];
  hasTip?: boolean;
  tipAmount?: number;
  selectedTipOption?: string;
}

export interface OrderRequestPriceInfo {
  subtotal: number;
  taxRate: number;
  taxTotal: number;
  orderTotal: number;
  tipTotal: number;
  total: number;
}

export class OrderRequestItem {
  // #region -> Property <-
  // ----------

  /*
    Location
  */
  selectedLocation: FranchiseLocation;

  /*
    Packages
   */
  selectedPackageList: PackageItem[] = [];

  /*
    Order Id
   */
  orderId;

  /*
    Has tip
   */
  hasTip = false;

  /*
   Tip Amount
 */
  tipAmount = 0;
  /*
    Selected Tip Option
   */
  selectedTipOption = '';

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Constructor <-
  // ----------

  constructor(newItem: IOrderRequestItem) {
    //
    this.orderId = newItem.orderId || undefined;
    this.selectedLocation = newItem.selectedLocation || undefined;
    this.selectedPackageList = newItem.selectedPackageList || [];
    this.tipAmount = newItem.tipAmount;
    this.hasTip = newItem.hasTip;
    this.selectedTipOption = newItem.selectedTipOption;
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method - Immutable <-
  // ----------

  json(): IOrderRequestItem {
    //
    return lodash.cloneDeep({
      orderId: this.orderId,
      selectedLocation: lodash.cloneDeep(this.selectedLocation),
      selectedPackageList: lodash.cloneDeep(this.selectedPackageList),
      hasTip: this.hasTip,
      tipAmount: this.tipAmount,
      selectedTipOption: this.selectedTipOption,
    });
  }

  clone(): OrderRequestItem {
    //
    return new OrderRequestItem(this.json());
  }

  update(updatedData: Partial<IOrderRequestItem>): OrderRequestItem {
    //
    const newData: IOrderRequestItem = { ...this.json(), ...updatedData };

    // Return a new reference
    return new OrderRequestItem(newData);
  }

  // ----------
  // #endregion

  /////////////////////////////

  // #region -> Method <-
  // ----------

  addNewPackage(packageItem: PackageItem): OrderRequestItem {
    //
    this.selectedPackageList.push(packageItem);

    //
    return this.clone();
  }

  removePackage(packageItem: PackageItem): OrderRequestItem {
    //
    lodash.remove(this.selectedPackageList, { id: packageItem.id });

    //
    return this.clone();
  }

  includePackage(packageItem: PackageItem): boolean {
    //
    for (const item of this.selectedPackageList) {
      if (packageItem.id === item.id) {
        return true;
      }
    }

    return false;
  }

  calculatePriceInfo(): OrderRequestPriceInfo {
    //
    if (this.selectedPackageList.length !== 0 && this.selectedLocation) {
      const subtotal = lodash.sumBy(this.selectedPackageList, 'price');
      const taxRate = this.selectedLocation.tax;
      const hasTip = this.hasTip;
      let tipTotal = 0;
      if (hasTip) {
        tipTotal = this.tipAmount;
      }
      let taxTotal = 0;
      for (const item of this.selectedPackageList) {
        taxTotal += Math.round(item.taxableAmount * taxRate * 100) / 100;
      }
      //
      return {
        subtotal,
        taxRate,
        taxTotal,
        get orderTotal() {
          return Math.round((this.subtotal + this.taxTotal) * 100) / 100;
        },
        tipTotal,
        get total() {
          return (
            Math.round((this.subtotal + this.taxTotal + tipTotal) * 100) / 100
          );
        },
      };
    }

    // Return a default one
    return {
      subtotal: 0,
      taxRate: 0,
      taxTotal: 0,
      orderTotal: 0,
      tipTotal: 0,
      total: 0,
    };
  }

  // ----------
  // #endregion

  /////////////////////////////
}
